/*
 * OneOdio discount copy card (performance-optimized)
 * --------------------------------------------------
 * Same UX as before (hide the in-cart discount field, show a "copy ONENEW12" card,
 * normalize old promo codes/text) but WITHOUT the setInterval that re-walked the
 * entire <body> every 500ms for 10s. The expensive full-page text rewrite now runs
 * exactly once, during browser idle time, and the card is re-inserted only when the
 * cart actually changes.
 */
(() => {
  const CODE = 'ONENEW12';
  const SELECTOR = '[data-oneodio-discount-copy]';
  const PRODUCT_TEMPLATE = 'template-product';
  const CART_TEMPLATE = 'template-cart';

  const isProductPage = () => document.body.classList.contains(PRODUCT_TEMPLATE);
  const isCartPage = () => document.body.classList.contains(CART_TEMPLATE);

  const isVisible = (element) => {
    if (!element) return false;
    const rect = element.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  };

  const copyText = async (text) => {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return;
    }
    const input = document.createElement('textarea');
    input.value = text;
    input.setAttribute('readonly', '');
    input.style.cssText = 'position:fixed;top:-9999px;left:-9999px;';
    document.body.appendChild(input);
    input.select();
    document.execCommand('copy');
    input.remove();
  };

  const createDiscountCard = (placement) => {
    const card = document.createElement('div');
    card.className = 'oneodio-discount-copy';
    card.setAttribute('data-oneodio-discount-copy', placement);
    card.innerHTML = `
      <div class="oneodio-discount-copy__text">
        <span class="oneodio-discount-copy__label">New customer offer</span>
        <strong>Get 12% off your first order</strong>
        <span class="oneodio-discount-copy__note">Copy code ${CODE}, then paste it into the discount field in Shopify Checkout before payment. Some automatic offers cannot be combined.</span>
      </div>
      <div class="oneodio-discount-copy__actions">
        <button class="oneodio-discount-copy__button" type="button" aria-label="Copy discount code ${CODE}">
          <span class="oneodio-discount-copy__code">${CODE}</span>
          <span class="oneodio-discount-copy__action">Copy</span>
        </button>
      </div>
    `;

    const button = card.querySelector('button');
    const action = card.querySelector('.oneodio-discount-copy__action');
    button.addEventListener('click', async () => {
      try {
        await copyText(CODE);
        action.textContent = 'Copied';
        button.classList.add('is-copied');
        window.setTimeout(() => {
          action.textContent = 'Copy';
          button.classList.remove('is-copied');
        }, 1800);
      } catch {
        action.textContent = CODE;
      }
    });

    return card;
  };

  const hideNativeCartDiscount = () => {
    document.querySelectorAll('form[is="cart-discount"], .cart-discount').forEach((element) => {
      if (element.hidden) return;
      element.hidden = true;
      element.setAttribute('aria-hidden', 'true');
    });

    document.querySelectorAll('#CartDrawer button, #CartDrawer summary, .cart button, .cart summary').forEach((button) => {
      const label = (button.textContent || '').trim().toLowerCase();
      if (label !== 'discount' && label !== 'coupon code') return;
      if (button.hidden) return;
      button.hidden = true;
      button.setAttribute('aria-hidden', 'true');
      button.setAttribute('tabindex', '-1');
    });
  };

  const findProductTarget = () => {
    const forms = [...document.querySelectorAll('form[data-type="add-to-cart-form"], form[action*="/cart/add"]')];
    const form = forms.find(isVisible) || forms[0];
    if (form) return form;
    const buttons = [...document.querySelectorAll('button, [role="button"], a')];
    return buttons.find((button) => /add to cart|buy now|fast checkout/i.test(button.textContent || '') && isVisible(button));
  };

  const findCartTarget = (root = document) => {
    const checkout = root.querySelector('a[href="/checkout"], a[href*="/checkout"]');
    return checkout?.closest('.drawer__footer-buttons, .grid, form') || checkout;
  };

  const insertCard = (target, placement) => {
    if (!target || document.querySelector(`${SELECTOR}[data-oneodio-discount-copy="${placement}"]`)) return;
    const card = createDiscountCard(placement);
    target.parentElement?.insertBefore(card, target);
  };

  // One-time, idle-time normalization of legacy promo codes / copy across the page.
  const rewriteOldDiscountTextOnce = () => {
    document.querySelectorAll('body *:not(script):not(style):not(input):not(textarea)').forEach((element) => {
      if (element.children.length > 0) return;
      const text = element.textContent || '';
      if (!/ONENEW10|GEMPAGES30|10%\s*OFF|First Order:\s*12% OFF/i.test(text)) return;
      const next = text
        .replace(/ONENEW10/g, CODE)
        .replace(/GEMPAGES30/g, CODE)
        .replace(/10%\s*OFF/gi, '12% OFF')
        .replace(/First Order:\s*12% OFF/gi, 'New Customer: 12% OFF');
      if (next !== text) element.textContent = next;
    });
  };

  const inject = () => {
    hideNativeCartDiscount();

    if (isProductPage()) insertCard(findProductTarget(), 'product');
    if (isCartPage()) insertCard(findCartTarget(), 'cart-page');

    const cartDrawer = document.querySelector('#CartDrawer');
    if (cartDrawer) insertCard(findCartTarget(cartDrawer), 'cart-drawer');
  };

  // Coalesce re-injection into one pass per frame.
  let scheduled = false;
  const scheduleInject = () => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      inject();
    });
  };

  const init = () => {
    inject();

    // Re-insert the card only when the cart actually changes.
    document.addEventListener('cart:updated', scheduleInject);
    document.addEventListener('cart:refresh', scheduleInject);

    const drawer = document.getElementById('CartDrawer');
    if (drawer && 'MutationObserver' in window) {
      new MutationObserver(scheduleInject).observe(drawer, { childList: true, subtree: true });
    }

    // Heavy full-page text rewrite: run once, off the critical path.
    (window.requestIdleCallback || ((cb) => window.setTimeout(cb, 200)))(rewriteOldDiscountTextOnce);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
